// Catalog sync + one-time offline device provisioning (P9.2).
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { putCatalog, getMeta, setMeta, catalogCount } from "@/lib/offlineDb";

const DEVICE_KEY = "offline_device_id";

export function useCatalogSync(online) {
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);

  const refreshMeta = useCallback(async () => {
    setLastSynced(await getMeta("catalog_synced_at"));
    setCount(await catalogCount());
  }, []);

  // Provision the offline device once per browser/terminal (admin/manager have OFFLINE.CREATE;
  // cashiers reuse the persisted device_id provisioned by a manager).
  const ensureDevice = useCallback(async () => {
    let id = await getMeta(DEVICE_KEY);
    if (id) return id;
    try {
      const { data } = await api.post("/offline/devices", {
        name: `Web POS ${navigator.platform || "browser"}`,
        device_type: "Fixed POS", app_version: "web-1.0.0",
      });
      id = data?.id || data?._id;
      if (id) await setMeta(DEVICE_KEY, id);
      return id;
    } catch (_) {
      return null; // 403 for cashier without OFFLINE.CREATE — manager must provision
    }
  }, []);

  const syncCatalog = useCallback(async () => {
    if (!online) { setError("offline"); return; }
    if (!localStorage.getItem("reason_token")) return; // only when authenticated
    setSyncing(true); setError(null);
    try {
      const { data } = await api.get("/pos/scan/catalog?limit=20000");
      const products = data?.products || (Array.isArray(data) ? data : []);
      await putCatalog(products);
      await ensureDevice();
      await refreshMeta();
    } catch (e) {
      setError(e?.message || "sync failed");
    } finally {
      setSyncing(false);
    }
  }, [online, ensureDevice, refreshMeta]);

  useEffect(() => { refreshMeta(); }, [refreshMeta]);
  // Auto-sync the offline catalog ONLY on browsers already provisioned as a POS terminal
  // (a device_id exists). Regular admin/analytics users never trigger the heavy 20k-row catalog
  // fetch or device provisioning — first-time POS setup happens explicitly from Device Settings.
  useEffect(() => {
    if (!online || !localStorage.getItem("reason_token")) return;
    getMeta(DEVICE_KEY).then((deviceId) => {
      if (!deviceId) return; // not a POS device → skip heavy sync
      catalogCount().then((c) => { if (c === 0) syncCatalog(); });
    });
  }, [online, syncCatalog]);

  return { syncing, lastSynced, count, error, syncCatalog, ensureDevice };
}
